import { Font } from '@react-pdf/renderer';
import { logger } from '../../utils/logger';

// Register emoji source for PDF support with fallback handling
// Note: Disabled external emoji fetching due to CSP restrictions
// Using text-based emoji fallbacks instead
export const registerEmojiSource = () => {
  try {
    // Commenting out external emoji source to avoid CSP issues
    // The PDF will use text-based emojis which are supported by most modern systems
    /*
    Font.registerEmojiSource({
      format: 'png',
      url: 'https://cdn.jsdelivr.net/gh/twitter/twemoji@14.0.2/assets/72x72/',
      withVariationSelectors: false, // Disable variation selectors to avoid -fe0f issues
    });
    */
    logger.info('Using text-based emoji rendering for PDFs');
  } catch (error) {
    logger.warn('Failed to register emoji source for PDF:', error);
  }
};

// Emoji replacements mapping - problematic emojis to working alternatives
const EMOJI_REPLACEMENTS: { [key: string]: string } = {
  // Replace gendered emojis with neutral versions to avoid -fe0f variants
  '🤷‍♂️': '🤷',
  '🤷‍♀️': '🤷', 
  '🤦‍♂️': '🤦',
  '🤦‍♀️': '🤦',
  '🙋‍♂️': '🙋',
  '🙋‍♀️': '🙋',
  '🙎‍♂️': '🙎',
  '🙎‍♀️': '🙎',
  
  // Replace complex emojis that commonly have variation selector issues
  '❤️': '❤',  // Red heart without variation selector
  '♥️': '♥',   // Heart suit without variation selector
  
  // Complex heart combinations (zero-width joiner sequences)
  '❤️‍🔥': '❤',  // Heart on fire -> simple heart
  '❤‍🔥': '❤',   // Heart on fire (without variation selector) -> simple heart
  '❤️‍🩹': '❤',  // Mending heart -> simple heart
  '❤‍🩹': '❤',   // Mending heart (without variation selector) -> simple heart
  
  // Other complex emoji combinations that cause PDF issues
  '👨‍💻': '👨',  // Man technologist -> man
  '👩‍💻': '👩',  // Woman technologist -> woman
  '👨‍🚀': '👨',  // Man astronaut -> man
  '👩‍🚀': '👩',  // Woman astronaut -> woman
  '👨‍⚕️': '👨',  // Man health worker -> man
  '👩‍⚕️': '👩',  // Woman health worker -> woman
  '👨‍🎓': '👨',  // Man student -> man
  '👩‍🎓': '👩',  // Woman student -> woman
  '👨‍🏫': '👨',  // Man teacher -> man
  '👩‍🏫': '👩',  // Woman teacher -> woman
  '👨‍💼': '👨',  // Man office worker -> man
  '👩‍💼': '👩',  // Woman office worker -> woman
  '👨‍🔬': '👨',  // Man scientist -> man
  '👩‍🔬': '👩',  // Woman scientist -> woman
  '👨‍🎨': '👨',  // Man artist -> man
  '👩‍🎨': '👩',  // Woman artist -> woman
  '👨‍🍳': '👨',  // Man cook -> man
  '👩‍🍳': '👩',  // Woman cook -> woman
  '👨‍🎤': '👨',  // Man singer -> man
  '👩‍🎤': '👩',  // Woman singer -> woman
  
  // Family combinations
  '👨‍👩‍👧': '👪',  // Family: man, woman, girl -> family
  '👨‍👩‍👦': '👪',  // Family: man, woman, boy -> family
  '👨‍👩‍👧‍👦': '👪',  // Family: man, woman, girl, boy -> family
  '👨‍👨‍👦': '👪',  // Family: man, man, boy -> family
  '👩‍👩‍👧': '👪',  // Family: woman, woman, girl -> family
  
  // Flag combinations (these are particularly problematic)
  '🏴‍☠️': '🏴',  // Pirate flag -> black flag
  '🏳️‍🌈': '🏳',  // Rainbow flag -> white flag
  '🏳️‍⚧️': '🏳',  // Transgender flag -> white flag
  
  // Eye combinations
  '👁️‍🗨️': '👁',  // Eye in speech bubble -> eye
  '👁‍🗨': '👁',   // Eye in speech bubble (no selectors) -> eye
  
  '⭐': '⭐',   // Star
  '✨': '✨',   // Sparkles
  
  // Hearts and love symbols
  '💛': '💛',   // Yellow heart
  '💚': '💚',   // Green heart
  '💙': '💙',   // Blue heart
  '💜': '💜',   // Purple heart
  '🖤': '🖤',   // Black heart
  '🤍': '🤍',   // White heart
  '🤎': '🤎',   // Brown heart
  '💕': '💕',   // Two hearts
  '💖': '💖',   // Sparkling heart
  '💗': '💗',   // Growing heart
  '💘': '💘',   // Heart with arrow
  '💝': '💝',   // Heart with ribbon
  '💞': '💞',   // Revolving hearts
  '💟': '💟',   // Heart decoration
  
  // Symbols that can have variation selectors
  '☀️': '☀',    // Sun
  '⛅': '⛅',    // Sun behind cloud
  '☁️': '☁',    // Cloud
  '⛈️': '⛈',    // Cloud with lightning and rain
  '🌤️': '🌤',   // Sun behind small cloud
  '🌥️': '🌥',   // Sun behind large cloud
  '🌦️': '🌦',   // Sun behind rain cloud
  '🌧️': '🌧',   // Cloud with rain
  '⛄': '⛄',    // Snowman without snow
  '☃️': '☃',    // Snowman
  '🌟': '🌟',   // Glowing star
  '💫': '💫',   // Dizzy
  '⚡': '⚡',    // High voltage
  '☄️': '☄',    // Comet
  '💥': '💥',   // Collision
  '🔥': '🔥',   // Fire
  '🌊': '🌊',   // Water wave
  '💧': '💧',   // Droplet
  '💨': '💨',   // Dashing away
  
  // Arrows and directional symbols
  '➡️': '➡',    // Right arrow
  '⬅️': '⬅',    // Left arrow
  '⬆️': '⬆',    // Up arrow
  '⬇️': '⬇',    // Down arrow
  '↗️': '↗',    // Up-right arrow
  '↖️': '↖',    // Up-left arrow
  '↘️': '↘',    // Down-right arrow
  '↙️': '↙',    // Down-left arrow
  '↔️': '↔',    // Left-right arrow
  '↕️': '↕',    // Up-down arrow
  '🔄': '🔄',   // Counterclockwise arrows button
  '🔃': '🔃',   // Clockwise vertical arrows
  '🔁': '🔁',   // Repeat button
  '🔂': '🔂',   // Repeat single button
  
  // Check marks and crosses
  '✅': '✅',   // Check mark button
  '❌': '❌',   // Cross mark
  '✔️': '✔',    // Check mark
  '✖️': '✖',    // Multiplication sign
  '❎': '❎',   // Cross mark button
  '✳️': '✳',    // Eight-spoked asterisk
  '✴️': '✴',    // Eight-pointed star
  '❇️': '❇',    // Sparkle
  
  // Warning and attention symbols
  '⚠️': '⚠',    // Warning
  '🚸': '🚸',   // Children crossing
  '⛔': '⛔',    // No entry
  '🚫': '🚫',   // Prohibited
  '🚷': '🚷',   // No pedestrians
  '🚯': '🚯',   // No littering
  '🚳': '🚳',   // No bicycles
  '🚱': '🚱',   // Non-potable water
  '🔞': '🔞',   // No one under eighteen
  '📵': '📵',   // No mobile phones
  
  // Religious and cultural symbols
  '☪️': '☪',    // Star and crescent
  '✡️': '✡',    // Star of David
  '🔯': '🔯',   // Dotted six-pointed star
  '☸️': '☸',    // Wheel of dharma
  '☯️': '☯',    // Yin yang
  '✝️': '✝',    // Latin cross
  '☦️': '☦',    // Orthodox cross
  '☮️': '☮',    // Peace symbol
  '🕎': '🕎',   // Menorah
  '🔱': '🔱',   // Trident emblem
  '⚛️': '⚛',    // Atom symbol
  
  // Music and entertainment
  '🎵': '🎵',   // Musical note
  '🎶': '🎶',   // Musical notes
  '♪': '♪',     // Musical note
  '♫': '♫',     // Beamed eighth notes
  '🎼': '🎼',   // Musical score
  '🎤': '🎤',   // Microphone
  '🎧': '🎧',   // Headphone
  '📻': '📻',   // Radio
  '🎬': '🎬',   // Clapper board
  '🎭': '🎭',   // Performing arts
  
  // Sports and activities
  '⚽': '⚽',   // Soccer ball
  '🏀': '🏀',   // Basketball
  '🏈': '🏈',   // American football
  '⚾': '⚾',   // Baseball
  '🎾': '🎾',   // Tennis
  '🏐': '🏐',   // Volleyball
  '🏉': '🏉',   // Rugby football
  '🥎': '🥎',   // Softball
  '🏓': '🏓',   // Ping pong
  '🏸': '🏸',   // Badminton
  '🥅': '🥅',   // Goal net
  '⛳': '⛳',   // Flag in hole
  '🏹': '🏹',   // Bow and arrow
  '🎣': '🎣',   // Fishing pole
  '🤿': '🤿',   // Diving mask
  '🥊': '🥊',   // Boxing glove
  '🥋': '🥋',   // Martial arts uniform
  '🎽': '🎽',   // Running shirt
  '🛹': '🛹',   // Skateboard
  '🛷': '🛷',   // Sled
  '⛸️': '⛸',    // Ice skate
  '🥌': '🥌',   // Curling stone
  '🎿': '🎿',   // Skis
  '⛷️': '⛷',    // Skier
  '🏂': '🏂',   // Snowboarder
  '🪂': '🪂',   // Parachute
  
  // Food and drink symbols
  '🍽️': '🍽',   // Fork and knife with plate
  '🥄': '🥄',   // Spoon
  '🔪': '🔪',   // Kitchen knife
  '☕': '☕',   // Hot beverage
  '🫖': '🫖',   // Teapot
  '🍵': '🍵',   // Teacup without handle
  '🧊': '🧊',   // Ice cube
  '🥤': '🥤',   // Cup with straw
  '🧋': '🧋',   // Bubble tea
  '🍶': '🍶',   // Sake
  '🍾': '🍾',   // Bottle with popping cork
  '🍷': '🍷',   // Wine glass
  '🍸': '🍸',   // Cocktail glass
  '🍹': '🍹',   // Tropical drink
  '🍺': '🍺',   // Beer mug
  '🍻': '🍻',   // Clinking beer mugs
  '🥂': '🥂',   // Clinking glasses
  '🥃': '🥃',   // Tumbler glass
  
  // Time and calendar symbols
  '⏰': '⏰',   // Alarm clock
  '⏱️': '⏱',    // Stopwatch
  '⏲️': '⏲',    // Timer clock
  '🕰️': '🕰',   // Mantelpiece clock
  '⌚': '⌚',   // Watch
  '📅': '📅',   // Calendar
  '📆': '📆',   // Tear-off calendar
  '🗓️': '🗓',   // Spiral calendar
  '📋': '📋',   // Clipboard
  
  // Technology and communication
  '📱': '📱',   // Mobile phone
  '☎️': '☎',    // Telephone
  '📞': '📞',   // Telephone receiver
  '📟': '📟',   // Pager
  '📠': '📠',   // Fax machine
  '📡': '📡',   // Satellite antenna
  '🔋': '🔋',   // Battery
  '🔌': '🔌',   // Electric plug
  '💻': '💻',   // Laptop computer
  '🖥️': '🖥',   // Desktop computer
  '🖨️': '🖨',   // Printer
  '⌨️': '⌨',    // Keyboard
  '🖱️': '🖱',   // Computer mouse
  '🖲️': '🖲',   // Trackball
  '💽': '💽',   // Computer disk
  '💾': '💾',   // Floppy disk
  '💿': '💿',   // Optical disk
  '📀': '📀',   // DVD
  '🧮': '🧮',   // Abacus
  '🎥': '🎥',   // Movie camera
  '📹': '📹',   // Video camera
  '📷': '📷',   // Camera
  '📸': '📸',   // Camera with flash
};

/**
 * Helper function to replace problematic emojis with working alternatives
 * This prevents PDF generation failures due to emoji rendering issues
 */
export const preprocessTextForPDF = (text: string): string => {
  if (!text) return text;
  
  let processedText = text;
  Object.entries(EMOJI_REPLACEMENTS).forEach(([problematic, replacement]) => {
    processedText = processedText.replace(
      new RegExp(problematic.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'g'), 
      replacement
    );
  });

  return processedText;
};